import { useEffect, useState } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Megaphone } from "lucide-react";
import { ApiError } from "../lib/api/client";
import { listPatchNotes } from "../lib/api/endpoints";
import { formatDateTime } from "../lib/format";
import { isSafeActionUrl } from "../lib/safeUrl";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { PatchNoteDto } from "../types";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Card } from "./Card";
import { Label } from "./Label";

const PAGE_SIZE = 20;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

const markdownComponents: Components = {
  h1: ({ children }) => <h2 style={{ margin: "18px 0 8px", color: C.ink, fontSize: 20, lineHeight: 1.3 }}>{children}</h2>,
  h2: ({ children }) => <h2 style={{ margin: "16px 0 8px", color: C.ink, fontSize: 17, lineHeight: 1.3 }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ margin: "14px 0 6px", color: C.ink, fontSize: 14.5, lineHeight: 1.35 }}>{children}</h3>,
  p: ({ children }) => <p style={{ margin: "0 0 10px", color: C.gray, fontSize: 13.5, lineHeight: 1.65 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: "0 0 10px", paddingInlineStart: 20, color: C.gray, fontSize: 13.5, lineHeight: 1.7 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: "0 0 10px", paddingInlineStart: 20, color: C.gray, fontSize: 13.5, lineHeight: 1.7 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 3 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ color: C.ink }}>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  code: ({ className, children }) => (
    <code
      style={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.9em",
        ...(className ? {} : { background: C.faint, borderRadius: 5, padding: "1px 5px", color: C.ink }),
      }}
    >
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre
      style={{
        margin: "0 0 12px",
        padding: 12,
        overflowX: "auto",
        background: C.bg,
        border: `1px solid ${C.faint}`,
        borderRadius: 10,
        color: C.ink,
        fontSize: 12,
        lineHeight: 1.55,
      }}
    >
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote
      style={{
        margin: "0 0 12px",
        padding: "2px 0 2px 14px",
        borderInlineStart: `3px solid ${C.lime}`,
        color: C.gray,
        fontStyle: "italic",
      }}
    >
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => {
    if (!isSafeActionUrl(href)) return <span>{children}</span>;
    const external = href.startsWith("http");
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        style={{ color: C.ink, fontWeight: 700, textDecoration: "underline", textDecorationColor: C.lime, textDecorationThickness: 2 }}
      >
        {children}
      </a>
    );
  },
  hr: () => <hr style={{ margin: "14px 0", border: 0, borderTop: `1px solid ${C.faint}` }} />,
};

function PatchNoteCard({ note }: { note: PatchNoteDto }) {
  return (
    <article
      aria-label={`Patch note ${note.version}`}
      style={{ paddingBlock: 18, borderTop: `1px solid ${C.faint}` }}
    >
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <Badge bg={C.limeSoft} color={C.ink}>v{note.version}</Badge>
        <Badge bg={C.faint} color={C.gray}>Painel</Badge>
        <span style={{ color: C.light, fontSize: 11.5 }}>{formatDateTime(note.publishedAtUtc)}</span>
      </div>
      <h2 style={{ margin: "0 0 4px", color: C.ink, fontSize: 18, lineHeight: 1.3 }}>{note.title}</h2>
      {note.summary && (
        <p style={{ margin: "0 0 10px", color: C.gray, fontSize: 13, lineHeight: 1.55 }}>{note.summary}</p>
      )}
      <div className="patch-notes__content" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {note.content}
        </Markdown>
      </div>
    </article>
  );
}

export function PatchNotesTab() {
  const { accessToken } = useSession();
  const token = accessToken ?? "";
  const [notes, setNotes] = useState<PatchNoteDto[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setNotes(null);
    setError(null);
    setLoadMoreError(null);
    setTotalCount(0);
    setPage(1);
    listPatchNotes(token, { target: "panel", page: 1, pageSize: PAGE_SIZE })
      .then(result => {
        if (cancelled) return;
        setNotes(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(e => {
        if (!cancelled) setError(errorMessage(e, "Falha ao carregar as novidades"));
      });
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  const loadMore = async () => {
    if (!token || !notes || loadingMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await listPatchNotes(token, { target: "panel", page: nextPage, pageSize: PAGE_SIZE });
      setNotes(current => {
        const existingIds = new Set((current ?? []).map(note => note.id));
        return [...(current ?? []), ...result.items.filter(note => !existingIds.has(note.id))];
      });
      setTotalCount(result.totalCount);
      setPage(nextPage);
    } catch (e) {
      setLoadMoreError(errorMessage(e, "Falha ao carregar mais novidades"));
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = notes !== null && notes.length < totalCount;

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ display: "inline-grid", placeItems: "center", width: 30, height: 30, borderRadius: 9, background: C.limeSoft, color: C.ink }}>
          <Megaphone aria-hidden="true" size={16} />
        </span>
        <div>
          <Label style={{ margin: 0 }}>Patch notes</Label>
          <p style={{ margin: "2px 0 0", color: C.light, fontSize: 12 }}>Novidades e avisos de release do painel</p>
        </div>
      </div>

      {error ? (
        <div role="alert" style={{ marginTop: 14, color: C.red, fontSize: 12.5, lineHeight: 1.5 }}>
          <p style={{ margin: "0 0 8px" }}>{error}</p>
          <Button onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</Button>
        </div>
      ) : notes === null ? (
        <div style={{ marginTop: 14, color: C.light, fontSize: 13 }}>Carregando novidades...</div>
      ) : notes.length === 0 ? (
        <div style={{ marginTop: 14, color: C.light, fontSize: 13 }}>Nenhuma novidade publicada ainda.</div>
      ) : (
        <>
          {notes.map(note => <PatchNoteCard key={note.id} note={note} />)}
          {loadMoreError && <div role="alert" style={{ marginTop: 12, color: C.red, fontSize: 12.5 }}>{loadMoreError}</div>}
          {hasMore && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
              <Button onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
